import React from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';

import {
  GetAssetMapImagesByMapId,
  GetResourceValue,
  UpdateGameDocState,
  UpdateMapImagesAsync
} from '../../../utils/game-document';
import PopupMenu from '../../popupMenu';
import { MapContext } from '../map-context';
import { MapImage } from '../../../types/game-document';
import { MapConfigStore } from '../../../pages/game-designer';
import { MapIllustrationContext } from '../map-illustration-context';
import { GameDocumentContext } from '../../../contexts/game-document';
import { ZoneAssetId } from '../../../types/game-document/entities/zone';
import { MenuList } from '../../../features/game-document/maps/map-images-window';

const imageMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
  { classIcon: 'lock', textMenu: 'Lock', textClass: '' },
  { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
];

export const Images: React.FC<{
  mapType: 'openStreetMap' | 'satelliteMap' | 'illustrated' | 'googleMaps';
  searchContentText: string;
  handleMapImagesMenuSelected: (id: string, menu: MenuList) => void;
}> = ({ mapType, searchContentText, handleMapImagesMenuSelected }) => {
  const { zoneId, gameId } = useParams();

  const [mapConfig, setMapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = React.useContext(GameDocumentContext);
  const map = React.useContext(MapContext);
  const mapIllustration = React.useContext(MapIllustrationContext);

  const { gameDocument } = state;

  const mapImages = React.useMemo(() => {
    if (!gameDocument) return [];

    let mapAssetId = '';

    if (zoneId) {
      mapAssetId =
        gameDocument?.rules.worldMap.zones.find(
          (zone: ZoneAssetId) => zone.zoneAssId === zoneId
        )?.mapAssId ?? '';
      return GetAssetMapImagesByMapId(gameDocument!, mapAssetId)!;
    } else {
      mapAssetId = state?.gameDocument?.rules?.worldMap?.mapAssId!;
      return GetAssetMapImagesByMapId(gameDocument!, mapAssetId)!;
    }
  }, [gameDocument, zoneId]);

  const filteredMapImages = React.useMemo(() => {
    return mapImages.filter(
      (mapImage: MapImage) =>
        GetResourceValue(state.gameDocument!, mapImage.titleResId!)
          ?.toLowerCase()
          .includes(searchContentText.toLowerCase()) ?? mapImage
    );
  }, [mapImages, gameDocument, searchContentText]);

  const showMapImage = async (mapImage: MapImage, isVisible: boolean) => {
    if (gameDocument) {
      const mapContext = mapType === 'illustrated' ? mapIllustration : map;
      const selectedImage = mapContext
        .getAllLayers()
        .find((l) => l.get('id') === `image-${mapImage.id}`);
      if (selectedImage) selectedImage.setVisible(isVisible);

      mapImage.isVisible = isVisible;
      const result = await UpdateMapImagesAsync(gameDocument, mapImage);
      setState((state) => UpdateGameDocState(state, result));
    }
  };

  if (
    filteredMapImages.length === 0 ||
    (searchContentText && filteredMapImages.length === 0)
  )
    return null;

  return (
    <>
      <PanelBar
        onSelect={() => {
          setMapConfig((prev) => ({
            ...prev,
            mapContentImages: !prev.mapContentImages
          }));
        }}>
        <PanelBarItem
          id={'image-panel-bar'}
          expanded={mapConfig.mapContentImages}
          className={'panel-bar-custom'}
          title={
            <div className={'d-flex gap-2 align-items-center'}>
              <span
                className="material-symbols-outlined"
                style={{
                  color: '#003A64'
                }}>
                photo
              </span>
              Images
            </div>
          }>
          {filteredMapImages.map((mapImage: MapImage, index: number) => (
            <div
              key={mapImage.id}
              className={
                'd-flex w-100 justify-content-between align-items-center mt-1 gap-2'
              }>
              <div
                id={`imageList-${mapImage.id}`}
                className={'card w-100 border-0'}
                style={{
                  backgroundColor: '#003A64',
                  borderColor: '#e5633d',
                  borderWidth: '3px'
                }}>
                <div
                  className={
                    'card-body p-2 d-flex w-100 justify-content-between align-items-center gap-2'
                  }>
                  <div
                    className={'d-flex gap-2 text-light align-items-center'}
                    style={{
                      minWidth: 0,
                      flex: 1
                    }}>
                    <span className={'material-symbols-outlined flex-shrink-0'}>
                      photo
                    </span>
                    <span
                      title={GetResourceValue(
                        state.gameDocument!,
                        mapImage.titleResId ?? ''
                      )}
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}>
                      {GetResourceValue(
                        state.gameDocument!,
                        mapImage.titleResId ?? ''
                      )}
                    </span>
                  </div>
                  <div className={'flex-shrink-0'}>
                    <PopupMenu
                      id={index}
                      buttonColor="white"
                      menus={imageMenu.map((menu, idx) => {
                        if (idx === 1)
                          return {
                            classIcon: !mapImage.isLocked
                              ? 'lock'
                              : 'lock_open',
                            textMenu: !mapImage.isLocked ? 'Lock' : 'Un-lock'
                          };

                        return menu;
                      })}
                      onMenuSelected={(id, menu) => {
                        handleMapImagesMenuSelected(mapImages[index].id!, menu);
                      }}
                      iconClass={'k-icon k-i-more-vertical h5 mb-0 text-light'}
                    />
                  </div>
                </div>
              </div>

              <span
                onClick={() => showMapImage(mapImage, !mapImage.isVisible)}
                className={`${mapImage.isVisible ? 'text-primary' : 'text-danger'} material-symbols-outlined cursor-pointer text-decoration-none`}>
                <span className={'material-symbols-outlined'}>
                  {mapImage.isVisible ? 'visibility' : 'visibility_off'}
                </span>
              </span>
            </div>
          ))}
        </PanelBarItem>
      </PanelBar>
    </>
  );
};
